import { Options, XAxisPlotLinesOptions } from 'highcharts';

const numberFormatParts = new Intl.NumberFormat().formatToParts(1234.5);

export const defaultColors = [
  '#aaeeee',
  '#f45b5b',
  '#90ee7e',
  '#DDDF00',
  '#7E4DB1',
  '#7798BF',
  '#777777',
  '#ff0066',
  '#55BF3B',
  '#DF5353',
  '#058DC7',
  '#50B432',
  '#ED561B',
  '#24CBE5',
  '#64E572',
  '#FF9655',
  '#FFF263',
  '#6AF9C4',
];

const colorsAsRgba = [
  [170, 238, 238],
  [244, 91, 91],
  [144, 238, 126],
  [221, 223, 0],
  [126, 77, 177],
  [119, 152, 191],
  [119, 119, 119],
  [255, 0, 102],
  [85, 191, 59],
  [223, 83, 83],
  [5, 141, 199],
  [80, 180, 50],
  [237, 86, 27],
  [36, 203, 229],
  [100, 229, 114],
  [255, 150, 85],
  [255, 242, 99],
  [106, 249, 196],
];

/**
 * @param {number} steps amount of steps
 * @param {[number, number, number]} start color as rgb array
 * @param {[number, number, number]} end color as rgb array
 *
 * @returns {string[]}
 *
 * @see https://gist.github.com/av01d/538b3fffc78fdc273894d173a83c563f
 */
export const getColorSteps: (
  steps: number,
  start?: number[],
  end?: number[]
) => string[] = (
  steps: number,
  start = colorsAsRgba[0],
  end = colorsAsRgba[5]
) => {
  let alpha = 0;

  return [
    `rgba(${start.join(',')})`,
    ...Array.from({ length: steps }, () => {
      alpha += 1.0 / steps;

      return `rgb(${[
        Math.round(end[0] * alpha + (1 - alpha) * start[0]),
        Math.round(end[1] * alpha + (1 - alpha) * start[1]),
        Math.round(end[2] * alpha + (1 - alpha) * start[2]),
      ].join(',')})`;
    }),
  ];
};

const chartTheme: Options = {
  lang: {
    thousandsSep:
      numberFormatParts.find((i) => i.type === 'group')?.value ?? ',',
    decimalPoint:
      numberFormatParts.find((i) => i.type === 'decimal')?.value ?? '.',
  },
  colors: defaultColors,
  chart: {
    backgroundColor: '#141414',
    borderWidth: 1,
    borderRadius: 0,
    borderColor: '#333',
    plotShadow: false,
    spacingTop: 5,
    plotBorderWidth: 0,
    zoomType: 'x',
    style: {
      fontFamily: 'Avenir, Arial, sans-serif',
    },
  },
  title: {
    style: {
      color: '#e1f2f5',
      font: '17px Avenir, Arial, sans-serif',
    },
  },
  subtitle: {
    style: {
      color: 'rgb(174, 187, 192)',
      font: '14px Avenir, Arial, sans-serif',
    },
    y: 38,
  },
  xAxis: {
    ordinal: false,
    gridLineWidth: 0,
    lineColor: '#333',
    tickColor: '#333',
    labels: {
      style: {
        color: '#e1f2f5',
        fontWeight: 'normal',
        fontSize: '13px',
      },
    },
    title: {
      style: {
        color: '#e1f2f5',
        font: 'bold 15px Avenir, Arial, sans-serif',
      },
    },
  },
  yAxis: {
    alternateGridColor: undefined,
    minorTickInterval: null,
    gridLineColor: 'rgba(255, 255, 255, .1)',
    lineWidth: 0,
    tickWidth: 0,
    labels: {
      style: {
        color: '#e1f2f5',
        fontWeight: 'normal',
        fontSize: '13px',
      },
    },
    title: {
      style: {
        color: '#e1f2f5',
        font: 'bold 15px Avenir, Arial, sans-serif',
      },
    },
  },
  legend: {
    itemStyle: {
      color: '#c2c7d0',
      fontSize: '15px',
    },
    itemHoverStyle: {
      color: '#e1f2f5',
    },
    itemHiddenStyle: {
      color: '#666',
    },
    maxHeight: 60,
    navigation: {
      activeColor: '#333',
      inactiveColor: '#141414',
      arrowSize: 11,
      style: {
        color: '#e1f2f5',
        fontSize: '15px',
      },
    },
  },
  tooltip: {
    backgroundColor: '#202020',
    borderWidth: 5,
    borderColor: 'rgba(33%, 33%, 33%, 0.8)',
    borderRadius: 2,
    style: {
      color: '#e1f2f5',
    },
  },
  plotOptions: {
    series: {
      turboThreshold: 0,
      marker: {
        enabled: false,
      },
    },
    bar: {
      dataLabels: {
        style: {
          fontSize: '12px',
          color: '#e1f2f5',
          textOutline: '1.5px solid #222',
        },
        y: -1,
      },
    },
    line: {
      dataLabels: {
        color: '#e1f2f5',
      },
      marker: {
        lineColor: '#333',
      },
    },
    pie: {
      dataLabels: {
        color: '#e1f2f5',
        style: {
          color: '#e1f2f5',
          fontWeight: 'normal',
          fontSize: '14px',
        },
      },
    },
    spline: {
      marker: {
        lineColor: '#333',
      },
    },
    scatter: {
      marker: {
        lineColor: '#333',
      },
    },
    candlestick: {
      lineColor: 'fff',
    },
  },
  exporting: {
    buttons: {
      exportButton: {
        symbolFill: '#e1f2f5',
      },
      printButton: {
        symbolFill: '#e1f2f5',
      },
    },
  },
  // scroll charts
  rangeSelector: {
    buttonTheme: {
      fill: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0.4, '#888'],
          [0.6, '#555'],
        ],
      },
      stroke: '#747474',
      style: {
        color: '#e1f2f5',
        fontWeight: 'normal',
      },
      states: {
        hover: {
          fill: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0.4, '#BBB'],
              [0.6, '#888'],
            ],
          },
          stroke: '#747474',
          style: {
            color: 'white',
          },
        },
        select: {
          fill: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0.1, '#000'],
              [0.3, '#333'],
            ],
          },
          stroke: '#747474',
          style: {
            color: '#e1f2f5',
          },
        },
      },
    },
    inputStyle: {
      backgroundColor: '#141414',
      color: '#e1f2f5',
      fontSize: 'small',
      borderColor: '#4d4d4d',
    },
    labelStyle: {
      color: '#ff',
    },
  },
  navigator: {
    handles: {
      backgroundColor: '#141414',
      borderColor: '#d6d6d6',
    },
    outlineColor: '#4d4d4d',
    maskFill: 'rgba(16, 16, 16, 0.5)',
    series: {
      color: '#808a9c',
      lineColor: 'rgba(16,16,16,0.0)',
    },
  },
  scrollbar: {
    barBackgroundColor: {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0.4, '#888'],
        [0.6, '#555'],
      ],
    },
    barBorderColor: '#747474',
    buttonArrowColor: '#d6d6d6',
    buttonBackgroundColor: {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0.4, '#888'],
        [0.6, '#555'],
      ],
    },
    buttonBorderColor: '#747474',
    rifleColor: '#d6d6d6',
    trackBackgroundColor: {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, '#000'],
        [1, '#333'],
      ],
    },
    trackBorderColor: '#747474',
  },
  drilldown: {
    activeDataLabelStyle: {
      color: '#e1f2f5',
      fontWeight: 'normal',
      textOutline: 'none',
      textDecoration: 'none',
    },
  },
};

export const createPlotLine: (
  label: string,
  value: number
) => XAxisPlotLinesOptions = (label: string, value: number) => {
  return {
    label: {
      text: label,
      rotation: 0,
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: '#e1f2f5',
      },
    },
    value,
    dashStyle: 'Dash',
  };
};

export default chartTheme;
